import { html, LitElement, nothing, PropertyDeclaration, PropertyValues } from "lit";
import { customElement, property, query, state } from 'lit/decorators.js';
import { when } from "lit/directives/when.js";
import { localized, msg, str } from "@lit/localize";
import type { ElementInternals } from "element-internals-polyfill/dist/element-internals";

import style from './text-field.scss';
import { TooltipMixin } from "../mixins/tooltip";


/* eslint-disable @typescript-eslint/no-unused-vars */
type TextFieldType = 'email' | 'text' | 'password' | 'tel' | 'number' | 'url' | 'textarea';
type UnsupportedTextFieldType = 'date' | 'datetime-local' | 'file' | 'month' | 'time' | 'week' | 'color';
type InvalidTextFieldType = 'button' | 'checkbox' | 'hidden' | 'image' | 'radio' | 'range' | 'reset' | 'submit';
/* eslint-enable */

/**
 * A text field inspired by Material Design.
 *
 * @slot tooltip - Optional content to show in tooltip on the right of the text field
 *
 * @cssproperty [--accent=#0076AD] - focused element color
 */
@customElement('text-field')
@localized()
export class TextField extends TooltipMixin(LitElement) {
    static override shadowRootOptions: ShadowRootInit = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    };

    static override styles = style;

    /*
     * sources of attributes:
     * https://github.com/material-components/material-web/blob/main/textfield/internal/text-field.ts
     * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attributes
     */

    // section: basic visual attributes

    /**
     * The `<input>` type to use, defaults to "text".
     * Note that only some of the `<input>` types (and a `textarea` type) are supported,
     * whereas some other types *may* work, but with weird styling or visual artifacts.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types
     */
    // we have to inline typespec here as custom elements manifest builder cannot unpack the structure yet
    @property() type: 'email' | 'text' | 'password' | 'tel' | 'number' | 'url' | 'textarea' | 'date' | 'datetime-local' | 'file' | 'month' | 'time' | 'week' | 'color' = 'text';

    /**
     * The floating Material label of the textfield component. It informs the user
     * about what information is requested for a text field. It is aligned with
     * the input text, is always visible, and it floats when focused or when text
     * is entered into the textfield.
     */
    @property() label = '';

    /**
     * Conveys additional information below the text field, such as how it should
     * be used.
     */
    @property({attribute: 'supporting-text'}) supportingText = '';

    // section: behavior and type-specific attributes

    /**
     * The number of rows to display for a `type="textarea"` text field.
     * Defaults to 5.
     */
    @property({type: Number}) rows = 5;

    /**
     * The number of cols to display for a `type="textarea"` text field.
     * Defaults to 20.
     */
    @property({type: Number}) cols = 20;


    /**
     * Should the textarea be auto resized based on content?
     */
    @property({type: Boolean, attribute: 'auto-resize', reflect: true}) autoResize = false;


    /**
     * The minimum number of characters that can be entered.
     */
    @property({attribute: 'minlength', type: Number}) minLength: Optional<number> = null;

    /**
     * The maximum number of characters that can be entered.
     */
    @property({attribute: 'maxlength', type: Number}) maxLength: Optional<number> = null;

    /**
     * Input mode for virtual keyboard.
     */
    @property({attribute: 'inputmode'}) override inputMode = 'text';

    /**
     * Minimum value of input.
     */
    @property() min = '';

    /**
     * Maximum value of input.
     */
    @property() max = '';

    /**
     * Returns or sets the element's step attribute, which works with min and max
     * to limit the increments at which a numeric or date-time value can be set.
     */
    @property() step = '';

    // section: common and form control attributes

    /**
     * Indicates whether or not a user should be able to edit the text field's
     * value.
     */
    @property({reflect: true, attribute: 'readonly', type: Boolean}) readOnly = false;

    /**
     * Describes what, if any, type of autocomplete should be provided.
     */
    @property({reflect: true}) autocomplete: Optional<AutoFill> = null;

    /**
     * Indicates whether the text field is disabled.
     */
    @property({type: Boolean, reflect: true}) disabled = false;

    /**
     * Indicates whether filling the text field is required.
     */
    @property({type: Boolean, reflect: true}) required = false;

    /**
     * A regular expression the input value should match.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/pattern
     */
    @property() pattern = '';

    /**
     * Space-separated list of custom validators' names.
     * Available validators are listed in toolbox/validators.
     * Loads ValidatorsToolbox when used.
     */
    @property({attribute: 'ui-validators'}) uiValidators = '';

    /**
     * Name of this text field.
     */
    @property() name = '';

    /**
     * Initial value of the text field.
     */
    @property({attribute: 'value'}) initialValue = '';

    /**
     * Indicates whether the text field should receive focus automatically.
     */
    // eslint-disable-next-line lit/no-native-attributes
    @property({attribute: 'autofocus', type: Boolean}) override autofocus = false;

    @property({reflect: true, type: Boolean}) xl = false;

    /**
     * Indicates whether the text field value should be trimmed.
     * Value 'auto' means 'on' since version 1.10.0.
     */
    @property({reflect: true}) trim: 'on' | 'off' | 'auto' = 'auto';

    @query('input#input') private readonly inputElement?: HTMLInputElement;
    @query('#input') private readonly inputOrTextAreaElement?: HTMLInputElement | HTMLTextAreaElement;

    // section: value manipulation

    get valueAsNumber(): number {
        return this.inputElement?.valueAsNumber ?? NaN;
    }

    set valueAsNumber(value: number) {
        if (!this.inputElement) return;
        this.inputElement.valueAsNumber = value;
        this.value = this.inputElement.value;
    }

    get valueAsDate(): Optional<Date> {
        return this.inputElement?.valueAsDate ?? null;
    }

    set valueAsDate(value: Optional<Date>) {
        if (!this.inputElement) return;
        this.inputElement.valueAsDate = value;
        this.value = this.inputElement.value;
    }

    @state() value = '';
    @state() private hasError = false;
    @state() private dirty = false;
    @state() private touched = false;
    @state() private isPasswordShown = false;

    static readonly formAssociated = true;
    private _internals: ElementInternals;

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('invalid', this._onInvalid);
    }

    formResetCallback(){
        this.value = this.initialValue;
        this._internals.setFormValue(this.value);
        this.dirty = false;
    }

    // section: rendering

    override render() {
        return html`
            <div id="container">
                <div>
                    ${this.type == 'textarea' ?
                        html`
                            <div id="input-cont">
                                <textarea id="input" placeholder=" "
                                    @change=${this._onChange}
                                    @select=${this._redispatchEvent}
                                    @input=${this._onInput}
                                    @blur=${this._onBlur}
                                    @keydown=${this._onKeydown}
                                    .autocomplete=${this.autocomplete ?? nothing}
                                    minlength=${this.minLength ?? nothing}
                                    maxlength=${this.maxLength ?? nothing}
                                    rows=${this.rows}
                                    cols=${this.cols}
                                    inputmode=${this.inputMode}
                                    ?readonly=${this.readOnly}
                                    ?disabled=${this.disabled}
                                    ?required=${this.required}
                                    ?autofocus=${this.autofocus}
                                    aria-describedby=${this.supportingText ? 'supporting-text' : nothing}
                                    ?data-dirty=${this.dirty}
                                    .value=${this.value}
                                ></textarea>
                                <label for="input"><span>${this.label}</span></label>
                                <usos-icon id="error" icon-type="outlined" icon-name="error_outline"></usos-icon>
                            </div>
                            ` :
                        html`
                            <div id="input-cont">
                                <input id="input" placeholder=" "
                                    @change=${this._onChange}
                                    @select=${this._redispatchEvent}
                                    @input=${this._onInput}
                                    @keydown=${this._onKeydown}
                                    @blur=${this._onBlur}
                                    type=${this.type == 'password' && this.isPasswordShown ? 'text' : this.type}
                                    .autocomplete=${this.autocomplete}
                                    minlength=${this.minLength ?? nothing}
                                    maxlength=${this.maxLength ?? nothing}
                                    inputmode=${this.inputMode}
                                    min=${this.min}
                                    max=${this.max}
                                    .step=${this.step}
                                    ?readonly=${this.readOnly}
                                    ?disabled=${this.disabled}
                                    ?required=${this.required}
                                    ?autofocus=${this.autofocus}
                                    pattern=${this.pattern || nothing}
                                    .value=${this.value}
                                    aria-describedby=${this.supportingText ? 'supporting-text' : nothing}
                                    ?data-dirty=${this.dirty}
                                >
                                <label for="input"><span>${this.label}</span></label>
                                <usos-icon id="error" icon-type="outlined" icon-name="error_outline"></usos-icon>
                                ${when(this.type == 'password', () => html`
                                    <usos-icon
                                        @click=${this._toggleShowPassword} id="showPassword"
                                        icon-type="outlined" .iconName=${this.isPasswordShown ? 'visibility_off' : 'visibility'}
                                    ></usos-icon>
                                `)}
                            </div>
                        `
                    }
                    ${this.renderTooltip()}
                </div>
                ${when(this._someSupportAvailable(), this._renderSupport.bind(this))}
            </div>
        `;
    }

    private _renderSupport() {
        const supportingText = this.hasError ?
            this.inputOrTextAreaElement!.validationMessage :
            this.supportingText;
        return html`
            <div id="support">
                ${when(supportingText, () => html`<div id="supporting-text" ?data-error=${this.hasError}>${supportingText}</div>`)}
                ${when(this.maxLength, () => this._renderMaxLength())}
            </div>
        `;
    }

    private _renderMaxLength() {
        const length = this.value.length, maxLength = this.maxLength;
        return html`
            <div id="maxlength" aria-label=${msg(str`Limit ${maxLength}, wprowadzono ${length} znaków`)}>
                ${length} / ${maxLength}
            </div>`;
    }

    private _someSupportAvailable(): boolean {
        return Boolean(this.supportingText || this.maxLength) || Boolean(this.inputOrTextAreaElement && !this.checkValidity());
    }

    override updated(changedProperties: PropertyValues<this>): void {
        if (changedProperties.has('initialValue') && !this.touched) {
            this.value = this.initialValue;
        }
        // skip validity check on first update
        if (changedProperties.has('value') && changedProperties.get('value') !== undefined) {
            this._updateValidity();
        }
    }

    // section: event handlers and propagators

    private _onInput() {
        this.touched = true;
        this.value = this.inputOrTextAreaElement!.value;
        this._internals.setFormValue(this.value);
        if (this.type == 'textarea' && this.autoResize)
            this._resizeTextarea();
    }

    private _onKeydown(e: KeyboardEvent) {
        const submitWanted = e.key == 'Enter' &&
            (e.target instanceof HTMLInputElement || e.ctrlKey || e.metaKey);
        if (submitWanted && this._internals.form) {
            this.blur(); // fires onChange if change happened - important for custom validation
            const submitButton = this._internals.form.querySelector<HTMLElement>('input[type=submit], button[type=submit]');
            if (submitButton !== null) {
                this._internals.form.requestSubmit(submitButton);
            } else {
                this._internals.form.requestSubmit();
            }
        }
    }

    private _onBlur() {
        this.dirty = true; // after blur we start treating the input as significantly interacted with
        this._trim();
        this._updateValidity();
    }

    /**
     * When a form is submitted, invalid events are fired at each form control that is invalid.
     * The validity of submittable elements is checked before submitting their owner <form>,
     * or after the checkValidity() method of the element or its owner <form> is called.
     */
    private _onInvalid() {
        this.dirty = true;
        this.hasError = true;
    }

    override requestUpdate(name?: PropertyKey, oldValue?: unknown, options?: PropertyDeclaration) {
        super.requestUpdate(name, oldValue, options);
        // If any properties change, update the form value, which may have changed
        // as well.
        // Update the form value synchronously in `requestUpdate()` rather than
        // `update()` or `updated()`, which are async. This is necessary to ensure
        // that form data is updated in time for synchronous event listeners.
        //
        // In reality, here `this._internals` can be still undefined, because `requestUpdate()` can be called from the
        // constructor of the component before `this._internals` is initialized.
        // However, in that case, the form value will be set correctly in `firstUpdated()`, so we can ignore this case here.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this._internals?.setFormValue(this.value);
        if (name == 'type')
            this.isPasswordShown = false;
    }

    private _onChange(event: Event) {
        this.dirty = true;
        this._redispatchEvent(event);
    }

    /**
     * Re-dispatches an event.
     *
     * This function is useful for forwarding non-composed events, such as `change`
     * events.
     * @param event The event to re-dispatch.
     * @return Whether or not the event was dispatched (if cancelable).
     */
    private _redispatchEvent(event: Event) {
        // For bubbling events in SSR light DOM (or composed), stop their propagation
        // and dispatch the copy.
        if (event.bubbles && event.composed) {
            event.stopPropagation();
        }

        const copy = new Event(event.type, event);
        const dispatched = this.dispatchEvent(copy);
        if (!dispatched) {
            event.preventDefault();
        }
        return dispatched;
    }

    private _toggleShowPassword() {
        this.isPasswordShown = !this.isPasswordShown;
    }

    private _resizeTextarea() {
        const ta = this.inputOrTextAreaElement!;
        const offset = ta.offsetHeight - ta.clientHeight;
        ta.style.height = 'auto';
        ta.style.height = `${ta.scrollHeight + offset}px`;
    }

    // section: input validation

    private _updateValidity(beforeSignificantInteraction = false) {
        this._internals.setValidity(this.inputOrTextAreaElement!.validity, this.inputOrTextAreaElement!.validationMessage, this.inputOrTextAreaElement);
        if (!beforeSignificantInteraction) {
            this.hasError = !this.checkValidity();
        }
    }

    get validityAnchor(): HTMLElement | undefined {
        return this.inputOrTextAreaElement;
    }

    /**
     * Returns whether an element will successfully validate based on forms validation rules and constraints.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/willValidate)
     */
    get willValidate() {
        return this._internals.willValidate;
    }

    get validity() {
        return this._internals.validity;
    }

    /**
     * Returns whether a form will validate when it is submitted, without having to submit it.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/checkValidity)
     */
    checkValidity() {
        return this._internals.checkValidity();
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/reportValidity) */
    reportValidity() {
        const validity = this._internals.reportValidity();
        this._updateValidity();
        this.requestUpdate();
        return validity;
    }

    /**
     * Makes the selection equal to the current object.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/select)
     */
    select() {
        this.inputOrTextAreaElement?.select();
    }

    /**
     * Sets a custom error message that is displayed when a form is submitted.
     * @param error Sets a custom error message that is displayed when a form is submitted.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/setCustomValidity)
     */
    setCustomValidity(error: string) {
        this.inputOrTextAreaElement?.setCustomValidity(error);
        if (this.inputOrTextAreaElement) this._updateValidity();
        this.requestUpdate();
    }

    private _trim(){
        if (this.trim != 'off') {
            this.value = this.value.trim();
            this.inputOrTextAreaElement!.value = this.value;
        }

    }

    override firstUpdated(changed: PropertyValues) { // material-web:labs/behaviors/constraint-validation.ts
        super.firstUpdated(changed);
        if (this.type == 'email') this.uiValidators = [this.uiValidators, 'oracleemail'].join(' ');
        if (this.uiValidators) {
            USOS.UI.loadTool('Validators').then(
                () => { USOS.UI.Validators!.applyValidators(this); },
                () => { console.error("Couldn't load USOS.UI.Validators toolbox"); }
            );
        }
        // Sync the validity again when the element first renders, since the
        // validity anchor is now available.
        //
        // Elements that `delegatesFocus: true` to an `<input>` will throw an
        // error in Chrome and Safari when a form tries to submit or call
        // `form.reportValidity()`:
        // "An invalid form control with name='' is not focusable"
        //
        // The validity anchor MUST be provided in `internals.setValidity()` and
        // MUST be the `<input>` element rendered.
        //
        // See https://lit.dev/playground/#gist=6c26e418e0010f7a5aac15005cde8bde
        // for a reproduction.
        this._updateValidity(true);
    }

}


declare global {
    interface HTMLElementTagNameMap {
      "text-field": TextField,
    }
}
